import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'
import {
  fetchStripeConnect,
  startStripeConnectOnboarding,
  syncStripeConnect,
  updateStripeAutoWithdraw,
  type StripeAutoWithdraw,
  type StripeConnectStatus,
} from '@/services/stripeConnectApi'
import {
  fetchPaystackBanks,
  fetchPaystackPayout,
  resolvePaystackAccount,
  savePaystackRecipient,
  updatePaystackAutoWithdraw,
  type PaystackAutoWithdraw,
  type PaystackBank,
  type PaystackPayoutStatus,
} from '@/services/paystackPayoutApi'

const SCHEDULE_OPTIONS: { value: StripeAutoWithdraw; label: string; hint: string }[] = [
  { value: 'off', label: 'Off (manual only)', hint: 'Request withdrawals yourself from Withdrawals.' },
  { value: 'daily', label: 'Daily', hint: 'Queue Available balance once per day.' },
  { value: 'weekly', label: 'Weekly', hint: 'Queue Available balance once per week.' },
  { value: 'monthly', label: 'Monthly', hint: 'Queue Available balance once per month.' },
]

function AutoWithdrawPicker({
  value,
  busy,
  onChange,
}: {
  value: string
  busy: boolean
  onChange: (schedule: StripeAutoWithdraw) => void
}) {
  return (
    <div>
      <h3 className="text-sm font-medium text-foreground">Auto-withdraw</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        When enabled, Kartly queues your Available balance on a schedule. Funds that are still on the way
        are included after they land. Platform admins still approve and disburse before money leaves Kartly
        (when approval is required).
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {SCHEDULE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={busy}
            onClick={() => onChange(opt.value)}
            className={`rounded-xl border px-3 py-3 text-left text-sm transition active:scale-[0.99] ${
              value === opt.value
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-muted/40'
            }`}
          >
            <span className="font-medium">{opt.label}</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">{opt.hint}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function StripePayoutPanel({
  storeId,
  status,
  busy,
  setBusy,
  setError,
  onStatus,
}: {
  storeId: number
  status: StripeConnectStatus
  busy: boolean
  setBusy: (v: boolean) => void
  setError: (v: string | null) => void
  onStatus: (s: StripeConnectStatus) => void
}) {
  async function onStartOnboarding() {
    setBusy(true)
    setError(null)
    try {
      const { url } = await startStripeConnectOnboarding(storeId)
      window.location.href = url
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not start bank account setup'))
      setBusy(false)
    }
  }

  async function onSync() {
    setBusy(true)
    setError(null)
    try {
      onStatus(await syncStripeConnect(storeId))
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not refresh payout status'))
    } finally {
      setBusy(false)
    }
  }

  async function onScheduleChange(schedule: StripeAutoWithdraw) {
    setBusy(true)
    setError(null)
    try {
      onStatus(await updateStripeAutoWithdraw(storeId, schedule))
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not update auto-withdraw'))
    } finally {
      setBusy(false)
    }
  }

  if (!status.stripeConfigured) {
    return (
      <p className="text-sm text-muted-foreground">
        Online payouts are not available on this platform yet. Contact support if you expected them to be
        enabled.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-muted/20 p-4">
        <p className="text-sm font-medium text-foreground">
          {status.readyForCheckout
            ? 'Ready — customers can pay by card, and you can withdraw to your bank account.'
            : status.connectAccountId
              ? 'Onboarding in progress — finish verification to receive payouts.'
              : 'Not set up — connect a bank account to receive withdrawals from Kartly.'}
        </p>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Ready to receive funds</dt>
            <dd className="font-medium">{status.chargesEnabled ? 'Yes' : 'No'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Bank payouts enabled</dt>
            <dd className="font-medium">{status.payoutsEnabled ? 'Yes' : 'No'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Details submitted</dt>
            <dd className="font-medium">{status.detailsSubmitted ? 'Yes' : 'No'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Payout account</dt>
            <dd className="font-mono text-xs">{status.connectAccountId ?? '—'}</dd>
          </div>
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" onClick={() => void onStartOnboarding()} disabled={busy}>
            {status.connectAccountId ? 'Continue setup' : 'Set up bank account'}
          </Button>
          {status.connectAccountId ? (
            <Button type="button" variant="outline" onClick={() => void onSync()} disabled={busy}>
              Refresh status
            </Button>
          ) : null}
        </div>
      </div>
      {status.readyForCheckout ? (
        <AutoWithdrawPicker value={status.autoWithdraw} busy={busy} onChange={(s) => void onScheduleChange(s)} />
      ) : null}
    </div>
  )
}

function PaystackPayoutPanel({
  storeId,
  status,
  busy,
  setBusy,
  setError,
  onStatus,
}: {
  storeId: number
  status: PaystackPayoutStatus
  busy: boolean
  setBusy: (v: boolean) => void
  setError: (v: string | null) => void
  onStatus: (s: PaystackPayoutStatus) => void
}) {
  const [banks, setBanks] = useState<PaystackBank[]>([])
  const [banksLoading, setBanksLoading] = useState(false)
  const [banksError, setBanksError] = useState<string | null>(null)
  const [bankCode, setBankCode] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [resolvedName, setResolvedName] = useState<string | null>(null)
  const [showReplace, setShowReplace] = useState(!status.readyForPayout)

  useEffect(() => {
    setShowReplace(!status.readyForPayout)
  }, [status.readyForPayout, status.recipientCode])

  useEffect(() => {
    if (!status.paystackConfigured || !status.currencyEligible) return
    let cancelled = false
    void (async () => {
      setBanksLoading(true)
      setBanksError(null)
      try {
        const list = await fetchPaystackBanks(storeId)
        if (!cancelled) setBanks(list)
      } catch (e) {
        if (!cancelled) {
          setBanks([])
          setBanksError(getUserFacingErrorMessage(e, 'Could not load banks'))
        }
      } finally {
        if (!cancelled) setBanksLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [storeId, status.paystackConfigured, status.currencyEligible])

  const bankOptions = useMemo(
    () =>
      banks.map((b) => ({
        value: b.code,
        label: b.name,
        keywords: b.code,
      })),
    [banks],
  )

  const selectedBankName = banks.find((b) => b.code === bankCode)?.name

  function resetFormFields() {
    setBankCode('')
    setAccountNumber('')
    setResolvedName(null)
  }

  async function onResolve() {
    setBusy(true)
    setError(null)
    setResolvedName(null)
    try {
      const r = await resolvePaystackAccount(storeId, { bankCode, accountNumber })
      setResolvedName(r.accountName)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not verify account'))
    } finally {
      setBusy(false)
    }
  }

  async function onSave() {
    setBusy(true)
    setError(null)
    try {
      const next = await savePaystackRecipient(storeId, {
        bankCode,
        accountNumber,
        accountName: resolvedName ?? undefined,
      })
      onStatus(next)
      resetFormFields()
      setShowReplace(false)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not save bank account'))
    } finally {
      setBusy(false)
    }
  }

  async function onScheduleChange(schedule: PaystackAutoWithdraw) {
    setBusy(true)
    setError(null)
    try {
      onStatus(await updatePaystackAutoWithdraw(storeId, schedule))
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not update auto-withdraw'))
    } finally {
      setBusy(false)
    }
  }

  if (!status.paystackConfigured) {
    return (
      <p className="text-sm text-muted-foreground">
        Online payouts are not available on this platform yet. Contact support if you expected them to be
        enabled.
      </p>
    )
  }

  const canVerify = Boolean(bankCode && accountNumber.length === 10)
  const canSave = canVerify && Boolean(resolvedName)
  const formOpen = showReplace || !status.readyForPayout

  return (
    <div className="space-y-6">
      <div
        className={`rounded-2xl border p-5 ${
          status.readyForPayout
            ? 'border-emerald-500/25 bg-emerald-500/5'
            : 'border-border bg-muted/20'
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Payout status
            </p>
            <p className="text-sm font-medium text-foreground">
              {status.readyForPayout
                ? 'Bank account connected'
                : 'Add a Nigerian bank account to receive wallet payouts'}
            </p>
            <p className="text-sm text-muted-foreground">
              {status.readyForPayout
                ? 'Kartly can send approved withdrawals to this account. You can replace it anytime.'
                : 'Customer card payments already work. This step is only for receiving your balance.'}
            </p>
          </div>
          <span
            className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
              status.readyForPayout
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {status.readyForPayout ? 'Ready' : 'Not set up'}
          </span>
        </div>

        {status.readyForPayout ? (
          <dl className="mt-4 grid gap-3 border-t border-border/60 pt-4 text-sm sm:grid-cols-2">
            <div className="rounded-xl bg-background/60 px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">Account name</dt>
              <dd className="mt-0.5 font-medium text-foreground">{status.accountName ?? '—'}</dd>
            </div>
            <div className="rounded-xl bg-background/60 px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">Account number</dt>
              <dd className="mt-0.5 font-medium tabular-nums text-foreground">
                ••••{status.accountNumberLast4 ?? '—'}
              </dd>
            </div>
            <div className="rounded-xl bg-background/60 px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">Bank</dt>
              <dd className="mt-0.5 font-medium text-foreground">
                {banks.find((b) => b.code === status.bankCode)?.name ?? status.bankCode ?? '—'}
              </dd>
            </div>
            <div className="rounded-xl bg-background/60 px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">Recipient</dt>
              <dd className="mt-0.5 truncate font-mono text-xs text-foreground">
                {status.recipientCode ?? '—'}
              </dd>
            </div>
          </dl>
        ) : null}

        {status.readyForPayout && !formOpen ? (
          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                resetFormFields()
                setShowReplace(true)
              }}
            >
              Replace bank account
            </Button>
          </div>
        ) : null}
      </div>

      {formOpen ? (
        <div className="space-y-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {status.readyForPayout ? 'Replace bank account' : 'Bank account'}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Search for your bank, enter the 10-digit NUBAN, then verify and save.
              </p>
            </div>
            {status.readyForPayout ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  resetFormFields()
                  setShowReplace(false)
                }}
              >
                Cancel
              </Button>
            ) : null}
          </div>

          {banksError ? <p className="text-sm text-destructive">{banksError}</p> : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="payout-bank" className="mb-2 block text-sm font-medium text-foreground">
                Bank
              </label>
              <SearchableSelect
                id="payout-bank"
                value={bankCode}
                onValueChange={(code) => {
                  setBankCode(code)
                  setResolvedName(null)
                }}
                options={bankOptions}
                placeholder="Search or select bank"
                searchPlaceholder="Type bank name…"
                emptyMessage="No banks match your search."
                noneLabel="Select bank"
                loading={banksLoading}
                disabled={busy}
                aria-label="Bank"
              />
              {selectedBankName ? (
                <p className="mt-1.5 text-xs text-muted-foreground">Selected: {selectedBankName}</p>
              ) : null}
            </div>

            <div className="sm:col-span-2">
              <Input
                id="payout-account-number"
                label="Account number"
                inputMode="numeric"
                autoComplete="off"
                disabled={busy}
                placeholder="10-digit NUBAN"
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 10))
                  setResolvedName(null)
                }}
              />
              <p className="mt-1.5 text-xs tabular-nums text-muted-foreground">
                {accountNumber.length}/10 digits
              </p>
            </div>
          </div>

          {resolvedName ? (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                Verified account name
              </p>
              <p className="mt-1 text-sm font-semibold text-foreground">{resolvedName}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Confirm this matches your business bank account before saving.
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Step 1: Verify account to fetch the name from your bank. Step 2: Save to enable payouts.
            </p>
          )}

          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={busy || !canVerify}
              onClick={() => void onResolve()}
              className="active:scale-[0.98]"
            >
              {busy ? 'Working…' : 'Verify account'}
            </Button>
            <Button
              type="button"
              disabled={busy || !canSave}
              onClick={() => void onSave()}
              className="active:scale-[0.98]"
            >
              Save bank account
            </Button>
          </div>
        </div>
      ) : null}

      {status.readyForPayout ? (
        <AutoWithdrawPicker
          value={status.autoWithdraw}
          busy={busy}
          onChange={(s) => void onScheduleChange(s)}
        />
      ) : null}
    </div>
  )
}

export default function StripeConnectPage() {
  const { currentStoreId: storeId } = useCurrentStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null)
  const [paystackStatus, setPaystackStatus] = useState<PaystackPayoutStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (storeId == null) {
      setStripeStatus(null)
      setPaystackStatus(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [stripe, paystack] = await Promise.all([
        fetchStripeConnect(storeId).catch(() => null),
        fetchPaystackPayout(storeId).catch(() => null),
      ])
      setStripeStatus(stripe)
      setPaystackStatus(paystack)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load payout setup'))
      setStripeStatus(null)
      setPaystackStatus(null)
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const connect = searchParams.get('connect')
    if (!connect || storeId == null) return
    void (async () => {
      try {
        await syncStripeConnect(storeId)
        await load()
      } catch {
        await load()
      } finally {
        const next = new URLSearchParams(searchParams)
        next.delete('connect')
        setSearchParams(next, { replace: true })
      }
    })()
  }, [searchParams, storeId, load, setSearchParams])

  const useStripe = Boolean(stripeStatus?.currencyEligible)
  const usePaystack = !useStripe && Boolean(paystackStatus?.currencyEligible)
  const neither =
    !loading &&
    storeId != null &&
    !useStripe &&
    !usePaystack &&
    (stripeStatus != null || paystackStatus != null)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Payouts"
          description="Set up where Kartly sends your store’s online payment balance. Verification is handled by our payout partner — this is separate from store setup."
        />

        {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}

        {loading ? (
          <div className="space-y-3" aria-busy="true" aria-label="Loading payout setup">
            <Skeleton className="h-24 w-full rounded-xl" />
            <SkeletonText lines={3} />
          </div>
        ) : storeId == null ? (
          <p className="text-sm text-muted-foreground">Select a store to manage payouts.</p>
        ) : useStripe && stripeStatus ? (
          <StripePayoutPanel
            storeId={storeId}
            status={stripeStatus}
            busy={busy}
            setBusy={setBusy}
            setError={setError}
            onStatus={setStripeStatus}
          />
        ) : usePaystack && paystackStatus ? (
          <PaystackPayoutPanel
            storeId={storeId}
            status={paystackStatus}
            busy={busy}
            setBusy={setBusy}
            setError={setError}
            onStatus={setPaystackStatus}
          />
        ) : neither ? (
          <p className="text-sm text-muted-foreground">
            Payout setup is available when your store currency is supported for held card payments (for
            example USD, GBP, EUR via card payouts, or NGN via Paystack).
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to load payout options for this store.</p>
        )}
      </Card>
    </div>
  )
}
